/**
 * ESC/POS printer profiles as the renderer sees them.
 *
 * The schema of record is `electron/shared/escpos-profile.ts` (imported here
 * as **types only**, like the bridge contract). The defaults are repeated here
 * because the renderer cannot import a value from `electron/**`; a unit test
 * pins this copy to the shared one so they cannot drift.
 */
import type { EscPosProfile, EscPosProfileInput } from 'electron-shared/escpos-profile';

export type { EscPosProfile, EscPosProfileInput };

/** Must equal `ESCPOS_PROFILE_DEFAULTS` in `electron/shared/escpos-profile.ts`. */
export const ESCPOS_PROFILE_DEFAULTS: EscPosProfile = {
  dotsPerLine: 576,
  imageEncoding: 'gs-v0',
  rasterBandRows: 256,
  cutter: 'partial',
  feedLinesBeforeCut: 4,
  drawer: { enabled: true, pin: 0, pulseOnMs: 120, pulseOffMs: 240 },
  statusQueries: true,
  idleCloseMs: 30_000,
};

export interface EscPosProfilePreset {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly profile: EscPosProfileInput;
}

/** Operator-facing starting points; every field stays editable afterwards. */
export const ESCPOS_PROFILE_PRESETS: readonly EscPosProfilePreset[] = [
  {
    id: 'generic-80mm',
    label: '80 mm (standard)',
    description: '576 dots wide, raster images, partial cut, cash drawer on pin 2.',
    profile: {},
  },
  {
    id: 'generic-58mm',
    label: '58 mm (narrow)',
    description: '384 dots wide, raster images, partial cut.',
    profile: { dotsPerLine: 384 },
  },
  {
    id: 'generic-80mm-legacy',
    label: '80 mm (older models)',
    description: 'ESC * bit-image mode for printers that do not accept GS v 0 raster images.',
    profile: { imageEncoding: 'esc-asterisk' },
  },
  {
    id: 'generic-80mm-no-status',
    label: '80 mm via print server',
    description: 'For printers or print servers that ignore status questions; prints without checking paper or cover.',
    profile: { statusQueries: false },
  },
];

/** The saved partial profile with every default filled in, for display. */
export function resolveEscPosProfileForDisplay(input: EscPosProfileInput | undefined): EscPosProfile {
  return {
    ...ESCPOS_PROFILE_DEFAULTS,
    ...(input ?? {}),
    drawer: { ...ESCPOS_PROFILE_DEFAULTS.drawer, ...(input?.drawer ?? {}) },
  };
}
